import React, { createContext, useContext, useState, useEffect, useMemo, useCallback, ReactNode } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useProjects } from '../hooks/useProjects';
import { useContextCoordinator } from '../hooks/useContextCoordinator';
import type { Project } from '../types/vrp';

interface ProjectContextType {
  currentProject: Project | null;
  setCurrentProject: (project: Project | null) => void;
  projects: Project[];
  isLoading: boolean;
  error: Error | null;
  // URL-based navigation functions
  restoreProjectFromUrl: (projectId: string) => void;
  navigateToProject: (projectId: number, path?: string) => void;
  getProjectUrl: (projectId: number, path: string) => string;
}

export const ProjectContext = createContext<ProjectContextType | undefined>(undefined);

export const ProjectProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [currentProject, setCurrentProject] = useState<Project | null>(null);
  const [isRestoringFromUrl, setIsRestoringFromUrl] = useState(false);
  const { data: projects = [], isLoading, error } = useProjects();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Context Update Coordination System - Prevent cascade conflicts
  const { coordinateUpdate, isContextUpdating } = useContextCoordinator();
  
  // Parse projectId from URL (both query parameters and path parameters) - Production-safe
  const getProjectIdFromUrl = (): string | null => {
    try {
      // Production-safe parsing with fallbacks
      const pathMatch = window.location.pathname.match(/\/projects\/(\d+)/);
      if (pathMatch?.[1]) return pathMatch[1];
      
      const searchParams = new URLSearchParams(window.location.search);
      return searchParams.get('project') || searchParams.get('projectId');
    } catch (error) {
      console.error('URL parsing error:', error);
      return null;
    }
  };

  // Coordinated project update function - Prevents cascade conflicts
  const setCurrentProjectCoordinated = useCallback(async (project: Project | null) => {
    // Skip if context is already updating to prevent cascade
    if (isContextUpdating('ProjectContext')) {
      console.log('🔒 [ProjectContext] Skipping project update - context already updating');
      return;
    }

    await coordinateUpdate('ProjectContext', () => {
      setCurrentProject(project);
    }, 1); // High priority for project updates
  }, [coordinateUpdate, isContextUpdating]);

  // URL-based navigation helper functions with enhanced error handling
  const restoreProjectFromUrl = useCallback((projectId: string): void => {
    coordinateUpdate('ProjectContext', async () => {
      try {
        console.log('🔄 [ProjectContext] Attempting to restore project from URL:', projectId);

        const parsedId = parseInt(projectId);
        if (!isNaN(parsedId) && projects.length > 0) {
          const foundProject = projects.find(p => p.id === parsedId);
          if (foundProject) {
            console.log('✅ [ProjectContext] Restoring project from URL:', foundProject.name);
            setCurrentProject(foundProject);
          } else {
            console.log('⚠️ [ProjectContext] Project not found in available projects for URL ID:', projectId);
          }
        } else {
          console.log('⚠️ [ProjectContext] Invalid project ID format from URL:', projectId);
        }
      } catch (error) {
        console.error('🚨 [ProjectContext] Error in restoreProjectFromUrl:', error);
      } finally {
        // Always clear the restoration flag to prevent hanging state
        setIsRestoringFromUrl(false);
      }
    }, 1); // High priority
  }, [projects, coordinateUpdate]); // Updated dependencies

  const navigateToProject = (projectId: number, path: string = ''): void => {
    const targetPath = path || location.pathname;
    
    // If path already contains project ID, use it as-is
    if (targetPath.includes(`/projects/${projectId}/`)) {
      navigate(targetPath);
      return;
    }
    
    // If path contains /projects/ with different ID, replace it
    const pathWithUpdatedProject = targetPath.replace(/\/projects\/\d+\//, `/projects/${projectId}/`);
    if (pathWithUpdatedProject !== targetPath) {
      navigate(pathWithUpdatedProject);
      return;
    }
    
    // Fallback: add query parameter for backward compatibility
    const searchParams = new URLSearchParams(location.search);
    searchParams.set('project', projectId.toString());
    const newUrl = `${targetPath}?${searchParams.toString()}${location.hash}`;
    navigate(newUrl);
  };

  const getProjectUrl = (projectId: number, path: string): string => {
    const searchParams = new URLSearchParams();
    searchParams.set('project', projectId.toString());
    return `${path}?${searchParams.toString()}`;
  };

  // Removed debug logging effect to reduce console noise and prevent loop triggers

  // URL parameter restoration effect - highest priority (memoized to prevent infinite loops)
  const urlProjectId = useMemo(() => getProjectIdFromUrl(), [location.pathname, location.search]);
  
  // Stabilized URL restoration to prevent infinite loops - KEY FIX
  const stabilizedRestoreFromUrl = useCallback(
    (projectId: string) => {
      if (isRestoringFromUrl) return; // Prevent concurrent updates
      setIsRestoringFromUrl(true);
      restoreProjectFromUrl(projectId);
    },
    [] // Remove unstable dependencies
  );
  
  // URL restoration effect with proper coordination - SIMPLIFIED
  useEffect(() => {
    if (!urlProjectId || projects.length === 0 || isRestoringFromUrl) {
      return;
    }
    
    // Only restore if project is different
    if (currentProject?.id !== parseInt(urlProjectId)) {
      stabilizedRestoreFromUrl(urlProjectId);
    }
  }, [urlProjectId, projects.length, stabilizedRestoreFromUrl]); // Minimal stable dependencies

  // Auto-select project if none selected and projects available - COORDINATED
  useEffect(() => {
    // Skip auto-selection if we're in the middle of restoring from URL or updating
    if (isRestoringFromUrl || isLoading || urlProjectId || isContextUpdating('ProjectContext')) {
      return; // Let URL restoration handle URL-based projects
    }

    if (!currentProject && projects.length > 0) {
      coordinateUpdate('ProjectContext', async () => {
        // Priority 1: Saved localStorage project
        const savedProjectId = localStorage.getItem('vrp-current-project-id');
        
        if (savedProjectId) {
          const parsedId = parseInt(savedProjectId);
          if (!isNaN(parsedId)) {
            const savedProject = projects.find(p => p.id === parsedId);
            if (savedProject) {
              console.log('✅ [ProjectContext] Restoring saved project:', savedProject.name);
              setCurrentProject(savedProject);
              return;
            }
          }
          // Clean up invalid saved project ID
          localStorage.removeItem('vrp-current-project-id');
        }

        // Priority 2: Default to first project
        const firstProject = projects[0];
        if (firstProject?.id != null && !isNaN(Number(firstProject.id))) {
          console.log('📌 [ProjectContext] Setting first project as current:', firstProject.name);
          setCurrentProject(firstProject);
        }
      }, 5); // Lower priority for auto-selection
    }
  }, [projects, currentProject, isLoading, isRestoringFromUrl, urlProjectId, coordinateUpdate, isContextUpdating]); // Updated dependencies

  // Save current project to localStorage - SIMPLIFIED and stable
  useEffect(() => {
    if (currentProject?.id != null && !isNaN(Number(currentProject.id))) {
      localStorage.setItem('vrp-current-project-id', String(currentProject.id));
    }
  }, [currentProject?.id]); // Minimal dependencies to prevent loops

  return (
    <ProjectContext.Provider value={{
      currentProject,
      setCurrentProject: setCurrentProjectCoordinated,
      projects,
      isLoading,
      error: error as Error | null,
      restoreProjectFromUrl,
      navigateToProject,
      getProjectUrl
    }}>
      {children}
    </ProjectContext.Provider>
  );
};

export const useProject = () => {
  const context = useContext(ProjectContext);
  if (context === undefined) {
    throw new Error('useProject must be used within a ProjectProvider');
  }
  return context;
};
